import { Edit2, Plus, Search, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import { MySelect } from "../../components/ui/Select";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { cn, formatCurrencyForTable } from "../../lib/utils";
import { SystemPlan, SystemPlanRequest, SystemPlanResponse } from "./@types";
import DeleteSystemModal from "./components/DeleteSystemPlan";
import SystemPlanModal from "./components/SystemPlanModal";
import { useGetSystemPlanMutation } from "./subscriptionApiSlice";

const PlanConfigDashboard: React.FC = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedPlanConfig, setSelectedPlanConfig] =
    useState<SystemPlan | null>(null);
  const [openModal, setOpenModal] = useState("");

  // RTK Query mutation
  const [getPlanConfig, { data: maintenanceData, isLoading }] =
    useGetSystemPlanMutation();

  const fetchPlanConfig = useCallback(async () => {
    const requestBody: SystemPlanRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      keyword: searchTerm || undefined,
      dateAdded: dateAddedFilter || undefined,
    };

    try {
      await getPlanConfig(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch expense heads:", err);
    }
  }, [currentPage, pageSize, searchTerm, getPlanConfig]);

  // Fetch expense heads on component mount and when filters change
  useEffect(() => {
    fetchPlanConfig();
  }, [currentPage, pageSize, searchTerm, dateAddedFilter, fetchPlanConfig]);

  // Table columns configuration
  const columns: Column<SystemPlan>[] = [
    {
      key: "name",
      header: "Name",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "features",
      header: "Features",
      width: "20",
      render: (value: number, item: SystemPlan) => (
        <span className="text-hColor">{item?.featuresList?.length || 0}</span>
      ),
    },
    {
      key: "monthlyPrice",
      header: "Monthly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "quarterlyPrice",
      header: "Quarterly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "yearlyPrice",
      header: "Yearly price",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "maxVehicles",
      header: "Max vehicles",
      width: "20",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "currency",
      header: "Currency",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{value ?? "Naira"}</span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      width: "20",
      render: (value: boolean) => (
        <span className="text-hColor">{value ? "Active" : "Inactive"}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: SystemPlan) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedPlanConfig(item);
                  setOpenModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedPlanConfig(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleModal = () => {
    setSelectedPlanConfig(null);
    setOpenModal("");
    fetchPlanConfig();
  };

  const response = maintenanceData?.data as SystemPlanResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All system plans config
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create
            </AppButton>
          </div>
        </div>

        {/* All expense heads section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={""}
                  options={[]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={dateAddedFilter}
                  onChange={(e) => setDateAddedFilter(e)}
                  onValChange={(e) => setDateAddedFilter(e)}
                  placeholder={"Filter"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    {
                      "opacity-50": isLoading,
                    },
                    "w-[70px] h-[42px] text-[13px] px-2 text-textColor appearance-none bg-white border border-[#CECECE] rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No system plans found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      <SystemPlanModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => handleModal()}
        plan={selectedPlanConfig ? selectedPlanConfig : null}
      />

      {selectedPlanConfig && (
        <DeleteSystemModal
          isOpen={openModal === "delete"}
          onClose={() => handleModal()}
          plan={selectedPlanConfig}
        />
      )}
    </div>
  );
};

export default PlanConfigDashboard;
